import { Prompt, Confirm } from "/WebInterface/new-ui/modules/alert.js";
import Loader from "/WebInterface/new-ui/modules/loader/loader.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";
import { initSharing } from "/WebInterface/new-ui/modules/modals/sharing/sharing.js";
import { initSearch } from "/WebInterface/new-ui/modules/modals/search/search.js";
import { initCopyLink } from "/WebInterface/new-ui/modules/modals/copy-link/copy-link.js";
import { initReportAProblem } from "/WebInterface/new-ui/modules/modals/report-a-problem/report-a-problem.js";
import { initCustomEventForm } from "/WebInterface/new-ui/modules/modals/custom-event-form/custom-event-form.js";
import { initCustomShare } from "/WebInterface/new-ui/modules/modals/custom-share/custom-share.js";
import { initQuickShare } from "/WebInterface/new-ui/modules/modals/quick-share/quick-share.js";
import { initUserOptions } from "/WebInterface/new-ui/modules/modals/user-options/user-options.js";
import { initSetMetaProperty } from "/WebInterface/new-ui/modules/modals/set-meta-property/set-meta-property.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { applyUserCustomizationOption } from "/WebInterface/new-ui/modules/customizations/customizations.js";
import HashListener from "/WebInterface/new-ui/modules/hash-listener.js";
import {
  showDownloadModal,
  downloadFiles,
} from "/WebInterface/new-ui/modules/advanced-download/advanced-download.js";
import store from "/WebInterface/new-ui/store/index.js";
import {
  operationStatus,
  fileTypes,
  mediaTypes,
  filesViewTypes,
  slideShowAllowedFilesExtensions,
} from "/WebInterface/new-ui/assets/js/constants.js";
import {
  apiCall,
  batchComplete,
} from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { initManageShares } from "/WebInterface/new-ui/manage-shares/js/middleware.js";
import { initRevisions } from "/WebInterface/new-ui/revisions/js/middleware.js";
import { onShowUploadModal } from "/WebInterface/new-ui/upload/js/middleware.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";
import { initSubscribeToEvents } from "/WebInterface/new-ui/modules/modals/subscribe-to-events/subscribe-to-events.js";
import { initEditFile } from "/WebInterface/new-ui/modules/modals/edit-file/edit-file.js";
import { initManageSubscribedEvents } from "/WebInterface/new-ui/modules/modals/manage-subscribed-events/manage-subscribed-events.js";
import { getCustomForm } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import {
  generateCustomForm,
  attachCustomFormTools,
} from "/WebInterface/new-ui/modules/custom-form/custom-form.js";
import Modal from "/WebInterface/new-ui/modules/modal.js";

const mergeContext = (items, context) => {
  if (items && items.length > 0 && context) {
    return [
      ...items.filter((item) => item.href_path !== context.href_path),
      context,
    ];
  } else if ((!items || items.length === 0) && context) {
    if (Array.isArray(context)) return context;
    return [context];
  }
  return items;
};

const selectedItems = (curPath, basket, first) => {
  if (basket) {
    return utils.getSelectedItems(store.basketDetails.files, "BASKET", first);
  } else return utils.getSelectedItems(store.files, curPath, first);
};

const getSelectedFiles = (args) => {
  const { context, loadedPath, curPath, fromBasket } = args;
  return mergeContext(
    selectedItems(loadedPath || curPath, fromBasket),
    context
  );
};

const expiredStatus = (status) => {
  return (
    status === operationStatus.COMPLETED ||
    status === operationStatus.FAILED ||
    status === operationStatus.CANCELED
  );
};

const safeSpecialChar = "_";
const pasteStatusPollTimeoutDuration = 1000;
let pasteStatusPollTimeout;
const BULK_OPERATION_BATCH_SIZE = 500;

const getBatchedItems = (items = [], batchSize = BULK_OPERATION_BATCH_SIZE) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const safeBatchSize = Math.max(1, Number(batchSize) || 1);
  const batchedItems = [];

  for (let i = 0; i < items.length; i += safeBatchSize) {
    batchedItems.push(items.slice(i, i + safeBatchSize));
  }

  return batchedItems;
};

const runBatchedAjaxCalls = async ({
  items = [],
  buildData,
  onBatchResponse,
  batchSize = BULK_OPERATION_BATCH_SIZE,
  timeout = 10000000,
}) => {
  const batchedItems = getBatchedItems(items, batchSize);
  let lastResponse = null;

  for (const itemBatch of batchedItems) {
    lastResponse = await ajax.call({
      timeout,
      data: buildData(itemBatch),
    });

    if (typeof onBatchResponse === "function") {
      onBatchResponse(lastResponse, itemBatch);
    }
  }

  return lastResponse;
};

const waitForPasteBatchCompletion = async (pasteId) => {
  const c2f = utils.cookie.get("currentAuth");

  while (true) {
    const resp = await ajax.call({
      timeout: 10000000,
      data: {
        command: "paste_status",
        id: pasteId,
        c2f,
      },
    });

    if (resp) {
      const xml = new DOMParser().parseFromString(resp, "text/xml");
      const result = utils.xmlToJson(xml);
      const response = result?.commandResult?.response;

      if (response) {
        const normalizedResponse = response.toLowerCase();
        const isComplete = normalizedResponse.indexOf("complete") >= 0;
        const isFailed =
          normalizedResponse.indexOf("cancelled") >= 0 ||
          normalizedResponse.indexOf("error") >= 0;

        if (!isComplete && !isFailed) {
          store.dispatch("pasteInProgress", {
            id: pasteId,
            response,
            action: "update",
          });
          await new Promise((resolve) =>
            setTimeout(resolve, pasteStatusPollTimeoutDuration)
          );
          continue;
        }

        store.dispatch("pasteInProgress", {
          id: pasteId,
          response,
          status: isComplete
            ? operationStatus.COMPLETED
            : operationStatus.FAILED,
          action: "update",
        });

        if (isFailed) {
          throw new Error(response);
        }

        return response;
      }
    }

    store.dispatch("pasteInProgress", {
      id: pasteId,
      response: "COMPLETED:OK",
      status: operationStatus.COMPLETED,
      action: "update",
    });
    return "COMPLETED:OK";
  }
};

const pasteStatusPoll = (pasteId) => {
  const c2f = utils.cookie.get("currentAuth");
  clearTimeout(pasteStatusPollTimeout);
  ajax
    .call({
      timeout: 10000000,
      data: {
        command: "paste_status",
        id: pasteId,
        c2f,
      },
    })
    .then((resp) => {
      if (resp) {
        const xml = new DOMParser().parseFromString(resp, "text/xml");
        const result = utils.xmlToJson(xml);
        if (result?.commandResult?.response) {
          const { response } = result.commandResult;
          if (
            response.toLowerCase().indexOf("cancelled") < 0 &&
            response.toLowerCase().indexOf("complete") < 0 &&
            response.toLowerCase().indexOf("error") < 0
          ) {
            store.dispatch("pasteInProgress", {
              id: pasteId,
              response,
              action: "update",
            });
            pasteStatusPollTimeout = setTimeout(() => {
              pasteStatusPoll(pasteId);
            }, pasteStatusPollTimeoutDuration);
          } else {
            store.dispatch("pasteInProgress", {
              id: pasteId,
              response,
              status:
                response.toLowerCase().indexOf("complete") >= 0
                  ? operationStatus.COMPLETED
                  : operationStatus.FAILED,
              action: "update",
            });
            console.log(
              "Updating paste status to completed, will refresh listing now"
            );
            const { pastedItems } = store;
            if (
              !pastedItems.find(
                (item) => item.status === operationStatus.ONGOING
              )
            ) {
              const nextItem = pastedItems.find(
                (item) => item.status === operationStatus.WAITING
              );
              if (nextItem) {
                userActions.paste({ fromQueue: nextItem });
              }
            }
          }
        } else {
          setTimeout(() => {
            console.log(
              "Updating paste status to completed, will refresh listing in 2 seconds"
            );
            store.dispatch("pasteInProgress", {
              id: pasteId,
              response: "COMPLETED:OK",
              status: operationStatus.COMPLETED,
              action: "update",
            });
            const { pastedItems } = store;
            if (
              !pastedItems.find(
                (item) => item.status === operationStatus.ONGOING
              )
            ) {
              const nextItem = pastedItems.find(
                (item) => item.status === operationStatus.WAITING
              );
              if (nextItem) {
                userActions.paste({ fromQueue: nextItem });
              }
            }
          }, 2000);
        }
      }
    })
    .catch((ex) => {
      console.log(ex);
      store.dispatch("pasteStatus", {
        id: pasteId,
        response: "ERROR",
      });
    });
};

const downloadZip = (selectedFiles) => {
  const defaultZipNameWhileDownloading =
    store.userInfo.userCustomizations?.defaultZipNameWhileDownloading?.value;

  const selectedPaths = [];
  selectedFiles.map((selectedFile) =>
    selectedPaths.push(
      `${utils.encodeURILocal(selectedFile.href_path)}${
        selectedFile.type === fileTypes.DIR ? "/" : ""
      }`
    )
  );

  const promptZipNameWhileDownloading = utils.getUserCustomizationValue(
    "promptZipNameWhileDownloading"
  );
  if (promptZipNameWhileDownloading) {
    Prompt({
      title: t("zipFileName"),
      inputAttrs: {
        value: "archive.zip",
      },
      confirm: t("ok"),
      cancel: t("cancel"),
    }).then((res) => {
      if (res) {
        let value = res?.value;
        if (utils.getFileExtension(value) !== "zip") {
          value += ".zip";
        }

        ajax.iframeSubmit({
          "#command": "downloadAsZip",
          "#zipName": value,
          "#paths": selectedPaths.join(":"),
        });
      }
    });
  } else {
    ajax.iframeSubmit({
      "#command": "downloadAsZip",
      "#zipName": defaultZipNameWhileDownloading
        ? `${defaultZipNameWhileDownloading}.zip`
        : "archive.zip",
      "#paths": selectedPaths.join(":"),
    });
  }
};

const userActionCustomFunction = {
  doCopy: (args, cutFlag) => {
    userActions.copy({ ...args, cut: cutFlag });
  },

  doPaste: (args) => {
    userActions.paste(args);
  },

  delete_items: (args) => {
    userActions.delete(args);
  },

  performRenameAction: (args) => {
    userActions.rename(args);
  },

  subscribeToEventsAction: (args) => {
    userActions.subscribeToEvents(args);
  },

  downloadAsZip: (args) => {
    userActions.download({ ...args, zip: true });
  },

  unzip: (args) => {
    userActions.download({ ...args, unzip: true });
  },

  shareFile: (args) => {
    userActions.share(args);
  },

  quickShareFile: (args) => {
    userActions.quickShare(args);
  },

  showDownloadBasket: (args) => {
    userActions.showBasket(args);
  },

  copypath: (args) => {
    userActions.copyDirectLink(args);
  },

  editFile: (args) => {
    userActions.edit(args);
  },

  Preview: (args) => {
    userActions.quickView(args);
  },
};

const userActions = {
  doLogout: () => {
    store.dispatch("logoutRequested");
  },
  performAction: (args) => {
    const { input } = args;
    if (input === "cut") {
      userActions["copy"].call(this, {
        ...args,
        cut: true,
      });
    } else if (input === "zip") {
      userActions["download"].call(this, { ...args, zip: true });
    } else if (input === "unzip") {
      userActions["zipItems"].call(this, { ...args, unzip: true });
    } else {
      if (userActions[input]) {
        userActions[input].call(this, { ...args });
      } else {
        growl(
          `${t("cantHandleThisActionJustYet")}. ${input} : ${args.name}`,
          `${t("workInProgress")}...`,
          5000,
          true
        );
      }
    }
  },
  invoke: (args) => {
    const { name } = args;
    try {
      const cmdName =
        name.indexOf("browserUploader") >= 0 ? "performAction('upload')" : name;
      const command = cmdName.split("(")[0].trim();
      const input =
        cmdName.split("'").length > 1 ? cmdName.split("'")[1].trim() : "";
      if (userActions[command]) {
        userActions[command].call(this, { input, ...args });
      } else {
        const action = new Function(cmdName);
        action();
      }
    } catch (error) {
      console.log(error);
      growl(t("cantHandleThisActionJustYet"), error, 5000, true);
    }
  },
  paste: (args) => {
    const { newName, fromQueue, skipRename } = args;
    const curPath = fromQueue ? fromQueue.curPath : args.curPath;
    const { copiedItems, pastedItems, files } = store;

    let type, items, copy_id;
    if (fromQueue) {
      type = fromQueue.type;
      items = fromQueue.items;
      copy_id = fromQueue.copy_id;
    } else {
      if (
        copiedItems &&
        copiedItems.type &&
        copiedItems.items &&
        copiedItems.items.length > 0
      ) {
        type = copiedItems.type;
        items = copiedItems.items;
        copy_id = copiedItems.copy_id;
      }
    }

    const copiedItemsNames = utils.deepCopy(copiedItems.names);
    delete copiedItems.names;

    const operationExists = pastedItems.find(
      (item) =>
        !expiredStatus(item.status) &&
        item.copy_id === copy_id &&
        item.curPath === curPath
    );

    if (operationExists) {
      console.log("Same operation exists", operationExists);
      return;
    }

    if (type && items && items.length > 0) {
      if (pastedItems.find((item) => item.status === operationStatus.ONGOING)) {
        const batchedItems = getBatchedItems(items);
        const totalBatches = batchedItems.length;
        const firstBatch = batchedItems[0] || [];
        const pasteInfo = {
          id: utils.getUId(),
          uiActivityId: copy_id,
          copy_id,
          ...copiedItems,
          items: firstBatch,
          batchItemsCount: firstBatch.length,
          totalItemsCount: items.length,
          batchNumber: 1,
          totalBatches,
          curPath,
          newName,
          status: operationStatus.WAITING,
          action: "add",
        };
        store.dispatch("pasteInProgress", pasteInfo);
      } else {
        const handlePaste = async (restParam) => {
          const doPaste = (formData = {}) => {
            store.dispatch("setCopiedItems", {
              copy_id: utils.getUId(),
              selectedPaths: [],
              selectedNames: [],
            });
            const c2f = utils.cookie.get("currentAuth");
            (async () => {
              let isQueueItemRemoved = false;
              try {
                const batchedItems = getBatchedItems(items);
                const totalBatches = batchedItems.length;
                for (
                  let batchIndex = 0;
                  batchIndex < batchedItems.length;
                  batchIndex++
                ) {
                  const itemBatch = batchedItems[batchIndex];
                  const resp = await ajax.call({
                    timeout: 10000000,
                    data: {
                      command: `${type}_paste`,
                      destPath: curPath.endsWith("/")
                        ? utils.encodeURILocal(curPath)
                        : utils.encodeURILocal(curPath) + "/",
                      ...restParam,
                      names: utils.encodeURILocal(itemBatch.join("\r\n") + "/"),
                      c2f,
                      ...formData,
                    },
                  });

                  if (!resp) {
                    throw new Error("Paste request failed.");
                  }

                  const xml = new DOMParser().parseFromString(resp, "text/xml");
                  const result = utils.xmlToJson(xml);
                  const pasteId = result?.commandResult?.response;

                  if (!pasteId) {
                    throw new Error("Paste request did not return status id.");
                  }

                  const pasteInfo = {
                    id: pasteId,
                    uiActivityId: copy_id,
                    copy_id,
                    ...copiedItems,
                    items: itemBatch,
                    batchItemsCount: itemBatch.length,
                    totalItemsCount: items.length,
                    batchNumber: batchIndex + 1,
                    totalBatches,
                    curPath,
                    newName,
                    status: operationStatus.ONGOING,
                    action: "add",
                  };

                  if (fromQueue && !isQueueItemRemoved) {
                    store.dispatch("pasteInProgress", {
                      ...fromQueue,
                      action: "remove",
                    });
                    isQueueItemRemoved = true;
                  }

                  store.dispatch("pasteInProgress", pasteInfo);
                  await waitForPasteBatchCompletion(pasteId);
                }
              } catch (ex) {
                console.log(ex);
                growl(
                  `There was some problem while performing ${type}-paste, please try again`,
                  "",
                  5000,
                  true
                );
              }
            })();
          };

          try {
            const handleSubmitButtonClick = (dialog) => {
              const validateFields = validate(dialog.root, false);
              if (validateFields.length === 0) {
                const formData = utils.formatFormDatesForApi(
                  dialog.root,
                  utils.serializeInputs(dialog.root)
                );

                dialog.hide(true);
                doPaste(formData);
              }
            };

            const handleReset = (dialog) => {
              const form = _$(dialog.root).find("form");
              form?.el()?.reset();
            };

            const formName = "pasteForm";
            const formData = await getCustomForm(formName);

            const formContent = generateCustomForm(formData);
            const pasteFormDialog = new Modal({
              title: t("Paste"),
              body: formContent,
              hasCloseOption: true,
              closable: true,
              uniqueId: "paste-form",
              minWidth: "800px",
              minHeight: "575px",
              buttons: [
                {
                  label: "reset",
                  close: false,
                  classes: ["mr-3", "is-bordered"],
                  onClick: () => {
                    handleReset(pasteFormDialog);
                  },
                },
                {
                  label: "submit",
                  close: false,
                  classes: ["mr-3"],
                  onClick: () => {
                    handleSubmitButtonClick(pasteFormDialog);
                  },
                },
              ],
            });

            attachCustomFormTools(pasteFormDialog.root, formName);
          } catch (error) {
            doPaste();
          }
        };

        if (copiedItemsNames?.length === 1 && !skipRename) {
          const [selectedFileName] = copiedItemsNames;
          const foundSameItem = files.find(
            (file) => file.name === selectedFileName
          );

          if (foundSameItem) {
            const promptForName = () => {
              Prompt({
                title: t(
                  "folderWithTheSameNameAlreadyExistsPleaseSelectDifferentName"
                ),
                confirm: t("Rename"),
                placeholder: "File name",
                cancel: t("cancel"),
                closable: true,
                inputAttrs: {
                  value: selectedFileName,
                },
              }).then((res) => {
                console.log(res);

                if (res === null) return;
                handlePaste({
                  newName: utils.encodeURILocal(res.value),
                });
              });
            };
            promptForName();
            return;
          }
        }
        handlePaste({
          newName: utils.encodeURILocal(newName),
        });
      }
    } else {
      growl(t("thereIsNothingToPaste"), t("Paste"), 5000, true);
    }
  },
  downloadLowRes: (args) => {
    const { context, fromBasket, curPath } = args;
    const selected = context || selectedItems(curPath, fromBasket);

    const paths = selected.map((file) => file.href_path);

    if (!selected || selected.length === 0) {
      growl(
        t("rightClickOnItemAndClickOnUpdateKeywords"),
        t("nothingSelected"),
        5000,
        true
      );
      return;
    }

    ajax.iframeSubmit({
      "#command": "getPreview",
      "#download": "true",
      "#size": "3",
      "#path": utils.encodeURILocal(paths.join(";")),
    });
  },
  downloadKeywords: (args) => {
    const { context, fromBasket, curPath } = args;
    const selected = context || selectedItems(curPath, fromBasket, true);
    if (!selected) {
      growl(
        t("rightClickOnItemAndClickOnUpdateKeywords"),
        t("nothingSelected"),
        5000,
        true
      );
      return;
    }
    ajax.iframeSubmit({
      "#command": "getKeywords",
      "#path": utils.encodeURILocal(selected.href_path),
    });
  },

  quickAdvancedDownload: (args) => {
    const selectedFiles = getSelectedFiles(args);

    if (selectedFiles.length === 0) {
      showDownloadModal();
      return;
    }

    const files = selectedFiles.filter((file) => file.type !== fileTypes.DIR);

    if (files.length !== selectedFiles.length) {
      growl(
        t("onlyAFileCanBeSelected"),
        t("downloadingFolderAreNotSupported"),
        5000,
        true
      );
      return;
    }

    // Validate file extensions for all selected files
    const onlyAllowedExtensionsToDownload = utils.getUserCustomizationValue(
      "onlyAllowedExtensionsToDownload",
      true
    );
    if (onlyAllowedExtensionsToDownload) {
      const disallowedFiles = files.filter((file) => {
        const extension = utils.getFileExtension(file);
        const isExtensionAllow = onlyAllowedExtensionsToDownload
          .toLowerCase()
          .split(",")
          .some(
            (ext) => ext.trim() === extension || ext.trim() === `.${extension}`
          );
        return !isExtensionAllow;
      });

      if (disallowedFiles.length > 0) {
        growl(
          disallowedFiles
            .map((disallowedFile) => disallowedFile.name)
            .join(", "),
          t("fileTypeNotAllowed"),
          5000,
          true
        );
        return;
      }
    }

    const notAllowedExtensionsToDownload = utils.getUserCustomizationValue(
      "notAllowedExtensionsToDownload",
      true
    );
    if (notAllowedExtensionsToDownload) {
      const disallowedFiles = files.filter((file) => {
        const extension = utils.getFileExtension(file);
        const isExtensionNotAllow = notAllowedExtensionsToDownload
          .toLowerCase()
          .split(",")
          .some(
            (ext) => ext.trim() === extension || ext.trim() === `.${extension}`
          );
        return isExtensionNotAllow;
      });

      if (disallowedFiles.length > 0) {
        growl(
          disallowedFiles
            .map((disallowedFile) => disallowedFile.name)
            .join(", "),
          t("fileTypeNotAllowed"),
          5000,
          true
        );
        return;
      }
    }

    // Check if any file requires advanced download
    const filesRequiringAdvancedDownload = files.filter((file) => {
      const downloadTypeByRangeCustomization = utils.getDownloadTransferType(
        file.size
      );
      return downloadTypeByRangeCustomization !== "Normal";
    });

    if (filesRequiringAdvancedDownload.length > 0) {
      downloadFiles(files);
      return;
    }

    // If all files are normal downloads, use traditional method
    if (files.length === 1) {
      const [file] = files;
      ajax.iframeSubmit({
        "#command": "download",
        "#path": utils.encodeURILocal(file.href_path),
      });
    } else {
      downloadZip(files);
    }
  },

  download: (args) => {
    const selectedFiles = getSelectedFiles(args);
    const { zip } = args;
    if (selectedFiles?.length > 0) {
      const extensions = selectedFiles.map((file) =>
        utils.getFileExtension(file)
      );

      const onlyAllowedExtensionsToDownload = utils.getUserCustomizationValue(
        "onlyAllowedExtensionsToDownload",
        true
      );
      if (onlyAllowedExtensionsToDownload) {
        const isExtensionAllow = onlyAllowedExtensionsToDownload
          .toLowerCase()
          .split(",")
          .some((ext) =>
            extensions.every((e) => ext.trim() === e || ext.trim() === `.${e}`)
          );

        if (!isExtensionAllow) {
          growl("", t("fileTypeNotAllowed"), 5000, true);
          return;
        }
      }

      const notAllowedExtensionsToDownload = utils.getUserCustomizationValue(
        "notAllowedExtensionsToDownload",
        true
      );
      if (notAllowedExtensionsToDownload) {
        const isExtensionNotAllow = notAllowedExtensionsToDownload
          .toLowerCase()
          .split(",")
          .some((ext) =>
            extensions.every((e) => ext.trim() === e || ext.trim() === `.${e}`)
          );

        if (isExtensionNotAllow) {
          growl("", t("fileTypeNotAllowed"), 5000, true);
          return;
        }
      }

      if (zip) {
        downloadZip(selectedFiles);
      } else {
        if (selectedFiles.length === 1 && selectedFiles[0].type === "FILE") {
          const canUseWebSocketForDownloading = utils.getUserCustomizationValue(
            "useWSForAdvancedDownloads"
          );

          const downloadTypeCustomization =
            store?.userInfo?.userCustomizations?.downloadTypeCustomization
              ?.value;

          if (!canUseWebSocketForDownloading && !downloadTypeCustomization) {
            ajax.iframeSubmit({
              "#command": "download",
              "#path": utils.encodeURILocal(selectedFiles[0].href_path),
            });

            return;
          }

          userActions.quickAdvancedDownload(args);
        } else {
          downloadZip(selectedFiles);
        }
      }
    } else {
      growl(t("nothingSelectedToDownload"), t("nothingSelected"), 5000, true);
    }
  },

  downloadAll: (args) => {
    const selected = store.files;

    if (!selected || selected.length === 0) return;

    if (selected.length == 1 && selected[0].type === "FILE") {
      growl("", t("downloadHasStarted"), 5000);
      ajax.iframeSubmit({
        "#command": "download",
        "#path": utils.encodeURILocal(selected[0].href_path),
      });
    } else {
      const selectedPaths = [];
      selected.map((selectedItem) =>
        selectedPaths.push(
          `${utils.encodeURILocal(selectedItem.href_path)}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        )
      );

      const promptZipNameWhileDownloading = utils.getUserCustomizationValue(
        "promptZipNameWhileDownloading"
      );

      if (promptZipNameWhileDownloading) {
        Prompt({
          title: t("zipFileName"),
          inputAttrs: {
            value: "archive.zip",
          },
          confirm: t("ok"),
          cancel: t("cancel"),
        }).then((res) => {
          if (res) {
            let value = res?.value;
            if (utils.getFileExtension(value) !== "zip") {
              value += ".zip";
            }

            growl("", t("downloadHasStarted"), 5000);
            ajax.iframeSubmit({
              "#command": "downloadAsZip",
              "#zipName": value,
              "#paths": selectedPaths.join(":"),
            });
          }
        });

        return;
      }

      growl("", t("downloadHasStarted"), 5000);
      ajax.iframeSubmit({
        "#command": "downloadAsZip",
        "#zipName": "archive.zip",
        "#paths": selectedPaths.join(":"),
      });
    }
  },
  zipItems: (args) => {
    const { context, curPath, unzip, fromBasket } = args;
    const selectedFiles = unzip
      ? context || selectedItems(curPath, fromBasket, true)
      : getSelectedFiles(args);

    if (!selectedFiles || selectedFiles.length === 0) {
      const message = unzip
        ? t("pleaseSelectFileToUnZip")
        : t("pleaseSelectFileToZip");
      growl(message, t("nothingSelected"), 5000, true);
      return;
    }

    const c2f = utils.cookie.get("currentAuth");
    const data = {
      command: unzip ? "unzip" : "zip",
      path: utils.encodeURILocal(curPath),
      c2f,
    };

    if (unzip) {
      if (selectedFiles.type === "DIR") return false;
      data.names = utils.encodeURILocal(selectedFiles.href_path);
    } else {
      if (selectedFiles.length > 0) {
        const selectedPaths = [];
        selectedFiles.map((selectedItem) =>
          selectedPaths.push(
            `${utils.encodeURILocal(selectedItem.href_path)}${
              selectedItem.type === fileTypes.DIR ? "/" : ""
            }`
          )
        );
        data.names = utils.encodeURILocal(selectedPaths.join("\r\n"));
      } else {
        return false;
      }
    }

    ajax
      .call({
        timeout: 10000000,
        data,
      })
      .always(() => {
        store.dispatch("refreshListing");
      })
      .then((resp) => {
        const xml = new DOMParser().parseFromString(resp, "text/xml");
        const result = utils.xmlToJson(xml);
        if (result && result.commandResult && result.commandResult.response) {
          const { response } = result.commandResult;
          growl(response, t("success"), 5000);
        }
      })
      .catch((ex) => {
        console.log(ex);
        growl(
          `There was some problem while ${
            unzip ? "unzipping" : "zipping"
          } selected items, please try again`,
          "",
          5000,
          true
        );
      });
  },
  quickView: (args) => {
    const { context, loadedPath, curPath, cb, fromBasket } = args;
    const selected = selectedItems(loadedPath || curPath, fromBasket);
    const selectedFiles = mergeContext(selected, context);
    const activeFileId = context ? context.id : undefined;
    const quickViewFiles = selected.length > 0 ? selectedFiles : store.files;
    store.dispatch("startQuickView", {
      files: quickViewFiles.filter(
        (item) =>
          item.preview > 0 ||
          item.mediaInfo.type === mediaTypes.VIDEO ||
          item.mediaInfo.type === mediaTypes.AUDIO ||
          slideShowAllowedFilesExtensions.includes(utils.getFileExtension(item))
      ),
      activeFileId,
    });
  },
  slideshow: (args) => {
    const { context, loadedPath, curPath, cb, fromBasket } = args;
    const selected = selectedItems(loadedPath || curPath, fromBasket);
    const selectedFiles = mergeContext(selected, context);
    const activeFileId = context ? context.id : undefined;
    const quickViewFiles = selected.length > 0 ? selectedFiles : store.files;

    store.dispatch("startSlideShow", {
      files: quickViewFiles.filter(
        (item) =>
          item.preview > 0 ||
          item.mediaInfo.type === mediaTypes.VIDEO ||
          item.mediaInfo.type === mediaTypes.AUDIO ||
          slideShowAllowedFilesExtensions.includes(utils.getFileExtension(item))
      ),
      activeFileId,
    });
  },
  addToBasket: (args) => {
    const { files: basketFiles } = store.basketDetails;
    const { fromBasket } = args;

    if (fromBasket) {
      return false;
    }
    const selectedFiles = getSelectedFiles(args);

    if (selectedFiles.length === 0 && basketFiles.length === 0) {
      growl(
        t("nothingSelectedToAddToTheBasket"),
        t("nothingSelected"),
        5000,
        true
      );
      return;
    }

    const doesFileExists = (file) => {
      return basketFiles.find(
        (curFile) => curFile.href_path === file.href_path
      );
    };

    const filesToAdd = selectedFiles.filter((file) => !doesFileExists(file));
    if (filesToAdd.length < selectedFiles.length)
      growl(
        t("selectedFileIsAlreadyAvailableInTheBasket"),
        t("fileAlreadyAddedToTheBasket"),
        5000,
        true
      );

    store.dispatch(
      "addFilesToBasket",
      filesToAdd.map((file) => ({
        ...file,
        isChecked: false,
        paddingLeft: null,
        ariaExpanded: false,
      }))
    );
  },
  showBasket: (args) => {
    const { fromBasket } = args;
    if (fromBasket) {
      return false;
    }
    store.dispatch("showBasket");
  },
  upload: (args) => {
    const { draggingEvent } = args;
    onShowUploadModal();

    if (draggingEvent) return;
    let href_path = null;
    if (args?.context?.type === "DIR") {
      href_path = args.context.href_path;
    }

    const iframe = document.querySelector("#fileUploadIframe");
    iframe?.contentWindow?.postMessage({
      key: "open",
      value: href_path,
    });
  },
  copy: (args) => {
    const { cut } = args;
    const selectedFiles = getSelectedFiles(args);

    const canCutCopyFilesOnly =
      utils.getUserCustomizationValue("cutCopyOnlyFiles");

    if (selectedFiles && selectedFiles.length > 0) {
      const selectedPaths = [];
      const selectedNames = [];
      let isFolderThere = false;
      selectedFiles.map((selectedItem) => {
        if (canCutCopyFilesOnly && selectedItem.type === fileTypes.DIR) {
          isFolderThere = true;
          return;
        }
        selectedPaths.push(
          `${selectedItem.href_path}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        );
        selectedNames.push(selectedItem.name);
      });

      if (isFolderThere) {
        growl(t("onlyFilesCanCutCopy"), "", 5000, true);
      }

      store.dispatch("setCopiedItems", {
        copy_id: utils.getUId(),
        selectedPaths,
        selectedNames,
        type: cut ? "cut" : "copy",
      });
    } else {
      growl(
        cut
          ? t("pleaseSelectFileOrFolderToCut")
          : t("pleaseSelectFileOrFolderToCopy"),
        t("nothingSelected"),
        5000,
        true
      );
    }
  },
  updateKeywords: (args) => {
    const selectedFiles = getSelectedFiles(args);
    if (selectedFiles && selectedFiles.length > 0) {
      const selectedPaths = [];
      selectedFiles.map((selectedItem) =>
        selectedPaths.push(
          `${selectedItem.href_path}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        )
      );
      const withKeyword = selectedFiles.find(
        (selectedItem) => selectedItem.keywords
      );
      const currentKeyword = withKeyword ? withKeyword.keywords : "";

      const generateHTMLParams = [
        {
          type: "small",
          text: `( ${t("htmlNotAllowedItWillStripDownHTMLWhileSaving")} )`,
        },
      ];

      if (selectedPaths.length > 1) {
        generateHTMLParams.push({
          type: "div.field",
          children: [
            {
              type: "label",
              children: [
                {
                  type: "input",
                  attrs: {
                    type: "checkbox",
                    name: "addToExisting",
                  },
                },
                {
                  type: "span",
                  text: ` ${t("appendKeywords")}`,
                },
              ],
            },
          ],
        });
      }

      Prompt({
        title: t("updatingKeywords"),
        textarea: true,
        submitOnEnter: false,
        confirm: t("save"),
        cancel: t("cancel"),
        zIndex: "101",
        inputAttrs: {
          value: currentKeyword
            .replace(/%0A/g, "\n")
            .replace(/%0D/g, "\r")
            .replace(/%22/g, '"'),
        },
        append: utils.generateHTML(generateHTMLParams),
        prepend: utils.generateHTML([
          {
            type: "div",
            children: [
              {
                type: "label.label",
                text: `${t("selectedFiles")} :`,
              },
              {
                type: "pre.scroll",
                text:
                  selectedPaths.length > 5
                    ? selectedPaths.slice(0, 5).join("\r\n") +
                      `\r\n(and ${selectedPaths.length - 5} more...)`
                    : selectedPaths.join("\r\n"),
              },
            ],
          },
        ]),
        closable: true,
      }).then((res) => {
        if (res) {
          const c2f = utils.cookie.get("currentAuth");
          const loading = new Loader({});
          (async () => {
            try {
              const resp = await runBatchedAjaxCalls({
                items: selectedPaths,
                buildData: (itemBatch) => ({
                  command: "editKeywords",
                  ...(res.addToExisting ? { append: res.addToExisting } : {}),
                  keywords: `${
                    res.addToExisting ? ", " : ""
                  }${utils.encodeURILocal(utils.sanitize(res.value))}`,
                  names: utils.encodeURILocal(itemBatch.join("\r\n")),
                  c2f,
                }),
              });
              console.log(resp);
              const xml = new DOMParser().parseFromString(resp, "text/xml");
              const result = utils.xmlToJson(xml);
              const { response } = result.commandResult;

              if (response.indexOf("Keywords Edited.") >= 0) {
                growl("", response, 5000, false);
              } else {
                growl("", response, 5000, true);
              }
            } catch (ex) {
              console.log(ex);
              growl(
                t(
                  "thereWasSomeProblemWhileUpdatingKeywordsForTheSelectedItemsPleaseTryAgain"
                ),
                "",
                5000,
                true
              );
            } finally {
              loading.deleteLoader();
              store.dispatch("refreshListing");
            }
          })();
        }
      });
    } else {
      growl(
        t("nothingSelectedToEditKeywords"),
        t("nothingSelected"),
        5000,
        true
      );
    }
  },

  delete: (args) => {
    const { privs } = store?.currentDirectoryInfo;
    const hasDirectoryDeletePermission = privs?.includes("(deletedir)");

    const selectedFiles = getSelectedFiles(args);
    if (selectedFiles && selectedFiles.length > 0) {
      const selectedPaths = [];
      let showCannotDeleteFolderGrowl = false;

      selectedFiles.forEach((selectedItem) => {
        const skipFolder =
          selectedItem.type === fileTypes.DIR && !hasDirectoryDeletePermission;

        if (skipFolder) {
          if (!showCannotDeleteFolderGrowl) showCannotDeleteFolderGrowl = true;
          return;
        }
        selectedPaths.push(
          `${selectedItem.href_path}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        );
      });

      if (showCannotDeleteFolderGrowl) {
        growl("", t("youDoNotHavePermissionToDeleteFolder"), 5000, true);
      }

      if (selectedPaths.length === 0) {
        return;
      }

      Confirm({
        title: t("areYouSureYouWantToDeleteSelectedItem"),
        confirm: t("yesDelete"),
        cancel: t("cancel"),
        zIndex: "101",
        append: utils.generateHTML([
          {
            type: "div",
            children: [
              {
                type: "label.label",
                text: `${t("selectedFiles")} :`,
              },
              {
                type: "pre.scroll",
                text:
                  selectedPaths.length > 5
                    ? selectedPaths.slice(0, 5).join("\r\n") +
                      `\r\n(and ${selectedPaths.length - 5} more...)`
                    : selectedPaths.join("\r\n"),
              },
            ],
          },
        ]),
        closable: true,
      }).then((res) => {
        if (res) {
          const c2f = utils.cookie.get("currentAuth");
          const loading = new Loader({});
          (async () => {
            try {
              const resp = await runBatchedAjaxCalls({
                items: selectedPaths,
                buildData: (itemBatch) => ({
                  command: "delete",
                  names: utils.encodeURILocal(itemBatch.join("\r\n")),
                  c2f,
                }),
              });
              console.log(resp);
            } catch (ex) {
              console.log(ex);
              growl(
                t(
                  "thereWasSomeProblemWhileDeletingSelectedItemsPleaseTryAgain"
                ),
                "",
                5000,
                true
              );
            } finally {
              loading.deleteLoader();
              store.dispatch("refreshListing");
            }
          })();
        }
      });
    } else {
      growl(
        t("pleaseSelectFileOrFolderToDelete"),
        t("nothingSelected"),
        5000,
        true
      );
    }
  },
  rename: (args) => {
    const { context, curPath, cb, fromBasket } = args;
    const selected = context || selectedItems(curPath, fromBasket, true);

    if (selected) {
      if (
        selected.vfs_protocol?.toUpperCase() === "S3" &&
        selected.type === fileTypes.DIR
      ) {
        growl(
          t("renameOptionIsUnavailableForS3Folders"),
          t("failure"),
          5000,
          true
        );
        return;
      }

      let rootPath = selected.root_dir;
      rootPath = rootPath || "/";
      if (!rootPath.endsWith("/")) rootPath += "/";

      Prompt({
        title: t("Rename"),
        confirm: t("Rename"),
        placeholder: "New name",
        cancel: t("cancel"),
        closable: true,
        inputAttrs: {
          value: selected.name,
        },
      }).then((res) => {
        if (res) {
          let name = res.value;
          if (name.toString().length === 0) {
            return;
          }
          name = name
            .replace(/\//g, safeSpecialChar)
            .replace(/:/g, safeSpecialChar);
          const c2f = utils.cookie.get("currentAuth");
          const loading = new Loader({});
          ajax
            .call({
              data: {
                command: "rename",
                path: utils.encodeURILocal(rootPath),
                name1: utils.encodeURILocal(selected.name),
                name2: utils.encodeURILocal(name),
                c2f,
              },
            })
            .always(() => {
              loading.deleteLoader();
              store.dispatch("refreshListing");
            })
            .then((resp) => {
              const xml = new DOMParser().parseFromString(resp, "text/xml");
              const result = utils.xmlToJson(xml);

              const response = result?.commandResult?.response;

              if (response?.toLowerCase()?.includes("error")) {
                growl(utils.decodeURILocal(response), "", 5000, true);
              }
            })
            .catch((ex) => {
              console.log(ex);
              growl(
                t(
                  "thereWasSomeProblemWhileRenamingSelectedItemsPleaseTryAgain"
                ),
                "",
                5000,
                true
              );
            });
        }
      });
    } else {
      growl(t("nothingSelectedToRename"), t("nothingSelected"), 5000, true);
    }
  },
  createFolder: (args) => {
    const { context, curPath, cb } = args;
    let rootPath = curPath;
    if (context && context.type === "FILE") {
      rootPath = context.root_dir;
    }
    rootPath = rootPath || "/";
    if (!rootPath.endsWith("/")) rootPath += "/";
    Prompt({
      title: t("pleaseEnterFolderName"),
      confirm: t("create"),
      placeholder: t("folderName"),
      body: `${t("creatingFolderAt")} ${utils.textEncode(rootPath)}`,
      cancel: t("cancel"),
      closable: true,
      prependFooter: utils.generateHTML([
        {
          type: "div.field.confirm-footer-field",
          children: [
            {
              type: "div.control",
              children: [
                {
                  type: "label.is-cursor-pointer",
                  children: [
                    {
                      type: "input@navigate#navigateToFolderOnCreate",
                      attrs: { type: "checkbox" },
                    },
                  ],
                  textAfter: ` ${t("navigateAfterCreating")}`,
                },
              ],
            },
          ],
        },
      ]),
    }).then((res) => {
      if (res) {
        let name = res.value;
        if (name.toString().length === 0) {
          return;
        }
        name = name
          .replace(/\//g, safeSpecialChar)
          .replace(/:/g, safeSpecialChar);
        let dirName = (rootPath || "/") + name;
        if (!dirName.endsWith("/")) dirName += "/";
        const c2f = utils.cookie.get("currentAuth");
        const loading = new Loader({});
        ajax
          .call({
            data: {
              command: "makedir",
              path: utils.encodeURILocal(dirName),
              c2f,
            },
          })
          .always(() => {
            loading.deleteLoader();
            if (res.navigate) {
              store.dispatch("navigateToDir", { path: dirName, type: "DIR" });
              return;
            }
            store.dispatch("refreshListing");
          })
          .then((resp) => {
            if (cb) {
              cb(dirName?.substring(0, dirName.length - 1));
            }
          })
          .catch((ex) => {
            console.log(ex);
            growl(
              t("thereWasSomeProblemWhileCreatingAFolderPleaseTryAgain"),
              "",
              5000,
              true
            );
          });
      }
    });

    applyUserCustomizationOption(
      store.userInfo.userCustomizations.autoNavigateToFolderOnCreation
    );
  },
  share: (args) => {
    const selectedFiles = getSelectedFiles(args);

    if (selectedFiles?.length > 0) {
      const ignoreFiles = utils.getUserCustomizationValue("shareIgnoreFiles");
      const ignoreFolders =
        utils.getUserCustomizationValue("shareIgnoreFolders");

      if (ignoreFiles && ignoreFolders) {
        return;
      }

      const selectedPaths = [];
      let hasFolder = false;

      selectedFiles.forEach((selectedItem) => {
        if (
          (ignoreFiles && selectedItem.type === fileTypes.FILE) ||
          (ignoreFolders && selectedItem.type === fileTypes.DIR)
        ) {
          return;
        }
        if (selectedItem.type === fileTypes.DIR) {
          hasFolder = true;
        }
        selectedPaths.push(
          `${selectedItem.href_path}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        );
      });

      if (selectedPaths.length === 0) {
        return;
      }

      const paths = selectedPaths.join("\r\n");
      const files =
        selectedPaths.length > 5
          ? selectedPaths.slice(0, 5).join("\r\n") +
            `\r\n(and ${selectedPaths.length - 5} more...)`
          : selectedPaths.join("\r\n");
      initSharing({
        files,
        paths,
        filesLength: selectedPaths.length,
        hasFolder,
      });
    } else {
      growl(t("nothingSelectedToShare"), t("nothingSelected"), 5000, true);
    }
  },
  search: (args) => {
    initSearch(args);
  },
  userOptions: () => {
    initUserOptions();
  },
  copyDirectLink: (args) => {
    const { context } = args;
    if (!context) return;

    const destinationPath =
      location.protocol +
      "//" +
      location.hostname +
      (location.port ? ":" + location.port : "") +
      "/";

    const { href_path, type } = context;

    const path =
      destinationPath +
      encodeURIComponent(href_path.replace(/^\//, "")) +
      (type === fileTypes.DIR ? "/" : "");

    if (path) initCopyLink(path);
  },
  createAndShareFolder: (args) => {
    const { input } = args;
    const customShareFieldsDetails = JSON.parse(utils.decodeURILocal(input));

    const formFieldsStatus = {};

    customShareFieldsDetails.visibleItems.forEach((item) => {
      formFieldsStatus[item] = true;
    });

    customShareFieldsDetails.hiddenItems.forEach((item) => {
      formFieldsStatus[item] = false;
    });

    const cb = (dirName) => {
      const path = dirName && !dirName.endsWith("/") ? dirName + "/" : dirName;
      initCustomShare({
        files: path,
        paths: path,
        filesLength: 1,
        customShareFieldsDetails: {
          ...customShareFieldsDetails,
          formFieldsStatus,
        },
      });
    };

    userActions.createFolder({ ...args, cb });
  },
  customShare: (args) => {
    const { input } = args;
    const customShareFieldsDetails = JSON.parse(utils.decodeURILocal(input));

    if (customShareFieldsDetails?.createAndShare) {
      userActions.createAndShareFolder(args);
      return;
    }

    const selectedFiles = getSelectedFiles(args);

    if (selectedFiles?.length > 0) {
      const selectedPaths = [];

      selectedFiles.map((selectedItem) => {
        selectedPaths.push(
          `${selectedItem.href_path}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        );
      });
      const paths = selectedPaths.join("\r\n");
      const files =
        selectedPaths.length > 5
          ? selectedPaths.slice(0, 5).join("\r\n") +
            `\r\n(and ${selectedPaths.length - 5} more...)`
          : selectedPaths.join("\r\n");

      const formFieldsStatus = {};

      customShareFieldsDetails.visibleItems.forEach((item) => {
        formFieldsStatus[item] = true;
      });

      customShareFieldsDetails.hiddenItems.forEach((item) => {
        formFieldsStatus[item] = false;
      });

      initCustomShare({
        files,
        paths,
        filesLength: selectedPaths.length,
        customShareFieldsDetails: {
          ...customShareFieldsDetails,
          formFieldsStatus,
        },
      });
    } else {
      growl(t("nothingSelectedToShare"), t("nothingSelected"), 5000, true);
    }
  },
  batchComplete: () => {
    const loading = new Loader({});

    batchComplete()
      .then((resp) => {
        console.log(resp);
        growl(t("transferAcknowledged"), t("result"), 5000);
      })
      .catch((err) => {
        console.log(err);
        growl("err", "", 5000, true);
      })
      .finally(() => {
        loading.deleteLoader();
      });
  },
  expandAllItems: () => {
    const activeView = utils.storage.get("activeView");
    if (activeView !== filesViewTypes.LIST) return;

    const { expandingOrCollapsingInProcess, allDashboardListingItemsExpanded } =
      store?.expandAllAndCollapseAllDetails;
    if (expandingOrCollapsingInProcess) return;

    if (allDashboardListingItemsExpanded) {
      store.dispatch("handleCollapseAllDashboardListing");
      return;
    }

    store.dispatch("handleExpandAllDashboardListing");
  },
  reportAProblem: (args) => {
    const selectedFiles = getSelectedFiles(args);
    const paths = selectedFiles.map((file) => file.href_path);
    initReportAProblem({ paths: paths.join("\n") });
  },
  customEvent: async (args) => {
    const { curPath, name } = args;
    const selectedFiles = getSelectedFiles(args);
    const paths = selectedFiles
      .map((selectedItem) => selectedItem.href_path)
      .join("/|");
    initCustomEventForm(name, paths || curPath);
  },
  exploreZip: (args) => {
    const { context, curPath, fromBasket } = args;

    const selected = context || selectedItems(curPath, fromBasket, true);
    if (!selected) {
      growl(t("pleaseSelectZipToExplore"), t("nothingSelected"), 5000, true);
      return;
    }

    const selectedFileExtension = utils.getFileExtension(selected);
    if (selectedFileExtension !== "zip") {
      growl(t("pleaseSelectZipToExplore"), t("nothingSelected"), 5000, true);
      return;
    }

    const hashListener = new HashListener({});
    hashListener.setPath(selected.href_path + "/");
  },
  csvExport: (args) => {
    const { curPath, loadedPath } = args;
    let selected = selectedItems(loadedPath || curPath);

    if (selected.length === 0) {
      selected = store.files;
    }

    const headers = [
      t("name"),
      t("path"),
      t("type"),
      t("size"),
      t("lastModified"),
      t("keywords"),
      t("created"),
    ];

    const blobParts = [
      new Blob([headers.join(",") + "\r\n"], { type: "text/csv" }),
    ];

    selected.forEach((file) => {
      const row = [
        file.name,
        file.href_path,
        file.type,
        parseInt(file.size) > 10 || file.type !== fileTypes.DIR
          ? parseInt(file.size)
          : "",
        `"${utils.formatDateForListing(file.modified, file.dateFormatted)}"`,
        `"${file.keywords}"`,
        `"${utils.formatDateForListing(file.created, file.createdDateFormatted)}"`,
      ];
      blobParts.push(new Blob([row.join(",") + "\r\n"], { type: "text/csv" }));
    });

    const blob = new Blob(blobParts, { type: "text/csv;charset=utf-8;" });
    const hashListener = new HashListener({});
    const path = hashListener
      .getPath()
      .replace(/[^\w\s]/gi, "_")
      .replace(/ /g, "_");

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `export_dir${path}.csv`;
    _$(link).hide();
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  },

  manageShares: () => {
    initManageShares();
  },

  subscribeToEvents: (args) => {
    const { context } = args;
    initSubscribeToEvents(context);
  },

  getHistory: (args) => {
    const { context, curPath, cb } = args;
    const selected = context || selectedItems(curPath, false, true);
    if (!selected) {
      growl(t("nothingSelectedToShare"), t("nothingSelected"), 5000, true);
      return;
    }

    initRevisions(selected);
  },

  quickShare: (args) => {
    const extractFileName = (hrefPath) => {
      const parts = hrefPath.split("/");
      return parts[parts.length - 1];
    };
    const selectedFiles = getSelectedFiles(args);

    if (selectedFiles && selectedFiles.length > 0) {
      const selectedPaths = [];

      selectedFiles.map((selectedItem) => {
        selectedPaths.push(
          `${selectedItem.href_path}${
            selectedItem.type === fileTypes.DIR ? "/" : ""
          }`
        );
      });
      const paths = selectedPaths.join("\r\n");

      const shareDetails = store.shareDetails;
      const customizationValues = store?.userInfo?.userCustomizations;

      const {
        EXPIREDAYS,
        EXPIREDAYSMAX,
        SENDEMAILDEFAULT,
        SHAREMETHOD,
        EMAILFROM,
        EMAILTO,
        EMAILCC,
        EMAILBCC,
        EMAILREPLYTO,
        EMAILSUBJECT,
        EMAILBODY,
      } = customizationValues;

      const getDefaultEmailTemplate = () => {
        const curValue = EMAILBODY?.value
          .replace(/\\r/g, "\r")
          .replace(/\\n/g, "\n");
        const nameBodySeparator = "``````";
        const templateSeparator = "^^^^^^";
        const _templates = curValue || "";
        const templates = _templates.split(templateSeparator);
        let defaultTemplate = "";

        for (let i = 0; i < templates.length; i++) {
          const splits = templates[i].split(nameBodySeparator);
          const [name = "Default", body] = splits;
          const isDefault = name.endsWith("_default");
          if (isDefault) defaultTemplate = body;
        }

        return defaultTemplate;
      };

      shareDetails.fullaccess =
        shareDetails.fullaccess ||
        utils.getUserCustomizationValue("quickShareFullAccess");

      let shareUsernamePermissionsStr = "(read)(resume)(view)(slideshow)";

      for (const key in shareDetails) {
        if (key.startsWith("priv_") && shareDetails[key]) {
          shareUsernamePermissionsStr += `(${key.replace("priv_", "")})`;
          if (!shareDetails.fullaccess) {
            if (key.replace("priv_", "") === "write") {
              shareUsernamePermissionsStr += `(rename)(makedir)`;
            } else if (key.replace("priv_", "") === "delete") {
              shareUsernamePermissionsStr += `(deletedir)`;
            }
          }
        }
      }

      let baseURL = document.location.href + "";
      if (baseURL.indexOf("#") >= 0)
        baseURL = baseURL.substring(0, baseURL.indexOf("#"));
      const customShareLinkURL = utils.getUserCustomizationValue(
        "customShareLinkURL",
        true
      );

      if (customShareLinkURL) {
        baseURL = customShareLinkURL;
      }

      const addDays = (days) => {
        const date = new Date();
        date.setDate(date.getDate() + Number(days));

        return date;
      };
      const date = addDays(
        !isNaN(EXPIREDAYS?.value) ? EXPIREDAYS?.value : 29 || 29
      );

      const parsed = shareDetails.txtDate
        ? flatpickr.parseDate(
            shareDetails.txtDate,
            utils.getFlatpickrDateFormat()
          )
        : null;
      const txtDate = parsed && !isNaN(parsed.getTime()) ? parsed : date;

      const defaultEmailBody =
        "A user would like to share a file with you.  This link will expire on {date} at {time}\r\n\r\n{web_link}";
      const defaultEmailSubject =
        "Sharing: A new file is being shared with you.";

      const obj = {
        command: "publish",
        allowUploads:
          shareDetails.fullaccess || shareDetails.priv_write || "false",
        attach: utils.getUserCustomizationValue("quickShareAttachThumbnail"),
        hide_download: shareDetails.hide_download || "false",
        attach_real: shareDetails.attach_real || "false",
        baseUrl: utils.encodeURILocal(baseURL),
        emailBcc: utils.stripDownEmails(
          shareDetails?.emailBcc || EMAILBCC?.value || ""
        ),
        emailReplyTo: utils.stripDownEmails(
          shareDetails?.emailReplyTo || EMAILREPLYTO?.value || ""
        ),
        emailBody: utils.encodeURILocal(
          shareDetails.shareBody ||
            getDefaultEmailTemplate() ||
            defaultEmailBody
        ),
        share_comments:
          utils.encodeURILocal(shareDetails?.share_comments) || "",
        emailCc: utils.stripDownEmails(
          shareDetails?.emailCc || EMAILCC?.value || ""
        ),
        emailFrom: utils.stripDownEmails(
          shareDetails?.emailFrom || EMAILFROM?.value || ""
        ),
        emailSubject: utils.encodeURILocal(
          shareDetails?.emailSubject ||
            EMAILSUBJECT?.value ||
            defaultEmailSubject
        ),
        emailTo: utils.stripDownEmails(
          shareDetails?.emailTo || EMAILTO?.value || ""
        ),
        shareUsername:
          (shareDetails.shareType || SHAREMETHOD?.value) === "internal",
        shareUsernames: shareDetails.shareUsernames || "",
        shareUsernamePermissions: utils.removeDuplicatesPermissions(
          shareUsernamePermissionsStr
        ),
        expire: utils.formatDateTimeForApi(txtDate, shareDetails.txtTime),
        paths: utils.encodeURILocal(paths),
        publishType: shareDetails.publishType || "reference",
        logins: shareDetails.logins || EXPIREDAYSMAX?.value || -1,
        sendEmail: utils.getUserCustomizationValue("quickShareSendEmail"),
        direct_link: utils.getUserCustomizationValue(
          "shareWindowFlagDirectLinkForFile"
        ),
      };

      const loading = new Loader({});
      apiCall("publish", {
        ...obj,
      })
        .then((response) => {
          growl(t("publishCompleted"), "", 5000);
          const quickShareDisableNotificationPopup =
            utils.getUserCustomizationValue(
              "quickShareDisableNotificationPopup"
            );

          if (quickShareDisableNotificationPopup) return;

          const { commandResult } = response;
          const fileNames = selectedPaths.map(extractFileName);
          initQuickShare(commandResult.response, obj, fileNames.join("%0D%0A"));
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          loading.deleteLoader();
        });

      return;
    }

    growl(t("nothingSelectedToShare"), t("nothingSelected"), 5000, true);
  },
  setMetaInfo: (args) => {
    const { context, curPath, fromBasket } = args;

    const selected = context || selectedItems(curPath, fromBasket, true);
    if (!selected) {
      growl("", t("nothingSelected"), 5000, true);
      return;
    }

    const path = utils.encodeURILocal(selected?.href_path);

    initSetMetaProperty({ path, ...args });
  },
  popupManageSync: (args) => {
    window.open("/WebInterface/sync/index.html");
    return;
  },

  edit: (args) => {
    const { context } = args;

    if (!context) {
      return;
    }

    initEditFile(context);
  },

  manageSubscribedEvents: () => {
    initManageSubscribedEvents();
  },

  ...userActionCustomFunction,
};

export default userActions;
